/**
 * Sprint 24.15 — shared visual primitives for the template hire flow.
 *
 * Category ramp colours, the template icon container, and the 3-step progress
 * strip — all per the Sprint 24.15 visual contract. Kept in one place so the
 * gallery, preview, customise, and entry surfaces never drift.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { AvailabilityStatus, TemplateRamp } from "../../hooks/use-agent-templates";

/** Icon-container background + icon colour per ramp (visual contract §gallery). */
export const RAMP_COLORS: Record<TemplateRamp, { bg: string; fg: string }> = {
  purple: { bg: "rgba(127, 119, 221, 0.14)", fg: "#7F77DD" },
  teal: { bg: "rgba(29, 158, 117, 0.14)", fg: "#1D9E75" },
  coral: { bg: "rgba(216, 90, 48, 0.14)", fg: "#D85A30" },
  pink: { bg: "rgba(212, 83, 126, 0.14)", fg: "#D4537E" },
  gray: { bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" },
};

export function rampFor(ramp?: TemplateRamp | null): { bg: string; fg: string } {
  return RAMP_COLORS[(ramp ?? "gray") as TemplateRamp] ?? RAMP_COLORS.gray;
}

/** Generic placeholder when a seed's icon glyph isn't in the bundled font. */
const FALLBACK_GLYPH = "ti-robot";

/** Square icon container holding a Tabler icon, tinted by the template ramp. */
export function TemplateIcon({
  icon,
  ramp,
  size = 44,
}: {
  icon?: string;
  ramp?: TemplateRamp | null;
  size?: number;
}) {
  const { bg, fg } = rampFor(ramp);
  const iconRef = useRef<HTMLElement>(null);
  const [broken, setBroken] = useState(false);

  // Sprint 24.15.1 (S24.15.1.03) — defensive fallback. A Tabler glyph that isn't
  // in the bundled font renders at zero width (the ti-schema / ti-template
  // bug). After the icon font is ready, measure the glyph on a layout frame; if
  // it's empty, swap to a generic placeholder so a future unbundled seed icon
  // never renders as a blank box. `=== 0` (not a threshold) avoids false positives
  // on real glyphs, which always have width.
  useEffect(() => {
    setBroken(false);
    let cancelled = false;
    const measure = () => {
      const el = iconRef.current;
      if (!el || cancelled) return;
      if (el.offsetWidth === 0) setBroken(true);
    };
    const onReady = () => {
      if (!cancelled) requestAnimationFrame(measure);
    };
    const fonts = document.fonts;
    if (fonts?.ready) fonts.ready.then(onReady).catch(() => {});
    else onReady();
    return () => {
      cancelled = true;
    };
  }, [icon]);

  const glyph = broken ? FALLBACK_GLYPH : icon || FALLBACK_GLYPH;
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "var(--border-radius-md)",
        background: bg,
        color: fg,
      }}
      aria-hidden="true"
    >
      <i ref={iconRef} className={`ti ${glyph}`} style={{ fontSize: Math.round(size * 0.45) }} />
    </div>
  );
}

/** 3-bar progress strip. `step` is 1-based (1=start, 2=select/compose, 3=confirm). */
export function WizardProgressStrip({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div style={progressWrap} role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step}>
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          style={{
            ...progressBar,
            background: i <= step ? "var(--color-text-primary)" : "var(--color-border-tertiary)",
          }}
        />
      ))}
    </div>
  );
}

const progressWrap: CSSProperties = {
  display: "flex",
  gap: 6,
  padding: "10px 18px 0",
};

const progressBar: CSSProperties = {
  flex: 1,
  height: 3,
  borderRadius: 2,
};

// ---------------------------------------------------------------------------
// Sprint 24.15.2 — equipment availability status (shared by modal + rows)
// ---------------------------------------------------------------------------

/**
 * Native capabilities every workspace already has (mirrors the worker's
 * NATIVE_KINDS) — they render as built-in, never as an installable connector.
 */
const NATIVE_KIND_RE = /^(workspace[\s_-]?memory|memory|kuvio[\s_-]?memory)$/i;
export function isNativeKind(kind: string): boolean {
  return kind.split(/\s+or\s+/i).some((k) => NATIVE_KIND_RE.test(k.trim()));
}

export interface EquipmentStatusMeta {
  /** Status-dot colour (CSS var). */
  dotColor: string;
  /** Short label shown after the kind name (may be empty). */
  statusLabel: string;
  /** Actionable CTA text for installable kinds; null for coming-soon / native. */
  ctaLabel: string | null;
  /** True when the kind can be installed today (drives clickable row + CTA). */
  installable: boolean;
}

/**
 * Resolve the render contract for an equipment kind's availability status
 * (S24.15.2.09 + visual contract). Native kinds (workspace-memory) short-circuit
 * to a green "Built-in" with no CTA regardless of the stored status.
 */
export function equipmentStatusMeta(
  status: AvailabilityStatus,
  note?: string | null,
  kind?: string,
): EquipmentStatusMeta {
  if (kind && isNativeKind(kind)) {
    return { dotColor: "var(--color-text-success)", statusLabel: "Built-in", ctaLabel: null, installable: false };
  }
  switch (status) {
    case "first-party":
      return { dotColor: "var(--color-text-success)", statusLabel: "", ctaLabel: "Install", installable: true };
    case "generic-mcp":
      return {
        dotColor: "var(--color-text-tertiary)",
        statusLabel: "via custom MCP",
        ctaLabel: "Install via custom MCP",
        installable: true,
      };
    case "wrapped-api":
      return {
        dotColor: "var(--color-text-tertiary)",
        statusLabel: "via OpenAPI wrap",
        ctaLabel: "Install via OpenAPI wrap",
        installable: true,
      };
    case "coming-soon":
    default:
      return {
        dotColor: "var(--color-text-warning)",
        statusLabel: note ? `Coming ${note}` : "Coming soon",
        ctaLabel: null,
        installable: false,
      };
  }
}

/** A small status dot (default 8px) tinted by availability status. */
export function EquipmentStatusDot({ color, size = 8 }: { color: string; size?: number }) {
  return (
    <span
      style={{ width: size, height: size, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }}
      aria-hidden="true"
    />
  );
}

/** Sprint 24.15.1 — the equipment-type pill (10px, secondary). */
export const typePillStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  padding: "2px 6px",
  borderRadius: 4,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  whiteSpace: "nowrap",
};

/**
 * Sprint 24.15.1 — the shared enriched equipment meta column: kind name + a
 * type pill on line 1, a one-line plain-English purpose on line 2. Used
 * identically by the customise + preview equipment rows so they never drift.
 */
export function EquipmentMeta({
  name,
  equipmentType,
  purpose,
}: {
  name: string;
  equipmentType?: string;
  purpose?: string;
}) {
  return (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{name}</span>
        {equipmentType ? <span style={typePillStyle}>{equipmentType}</span> : null}
      </div>
      {purpose ? (
        <span style={{ fontSize: 12, lineHeight: 1.4, color: "var(--color-text-tertiary)" }}>{purpose}</span>
      ) : null}
    </div>
  );
}
